import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { t } from 'i18next';

import { Api } from '../../../entries/ApiTransport';
import sagaAssessor from '../../../utils/sagaAssessor';
import { schedulerActions, schedulerTypes } from './actions';
import { toastActions } from 'Containers/Toasts/store';
import { homeActions } from 'Containers/Home/store/actions';
import { setError } from 'shared/Error/store/actions';
import {
  buildLocalAppointment,
  findDemoClient,
  getDemoAppointments,
  getDemoCalendarQueue,
  getDemoCalendarSlots,
} from 'demo';
import { getLiveDemoBundle, selectIsDemoMode } from 'demo/saga';
import { markAppointmentDeleted, markQueueRemoved, patchDemoAppointment } from 'demo/session';

const api = Api.getInstance();

/** Endpoint concordato con il backend: body { idListaAttesa }. */
export const QUEUE_REMOVE_ENDPOINT = '/business/removeFromQueue';
const QUEUE_ENTRY_NOT_FOUND = 'queueEntryNotFound';

const findAppointmentById = (appointments, id) => {
  const targetId = Number(id);
  if (!Number.isFinite(targetId)) return null;
  for (const dayTasks of Object.values(appointments || {})) {
    const found = (dayTasks || []).find((task) => Number(task.idprenotazione) === targetId);
    if (found) return found;
  }
  return null;
};

const buildOptimisticEditPayload = (existing, payload) => {
  const nextNota = payload.nota !== undefined ? payload.nota : existing?.nota;
  const next = {
    ...existing,
    idprenotazione: existing?.idprenotazione ?? payload.idPrenotazione,
    orario_start: payload.orario_start ?? existing?.orario_start,
    orario_end: payload.orario_end ?? existing?.orario_end,
    data_prenotazione: payload.data_prenotazione ?? existing?.data_prenotazione,
    poltrona: payload.poltrona ?? existing?.poltrona,
    idpoltrona: payload.poltrona ?? existing?.idpoltrona ?? existing?.poltrona,
    nota: nextNota,
  };

  // Prenotazione da salone con nota-come-nome: aggiorna subito anche il nome in card.
  if (
    payload.nota !== undefined &&
    existing &&
    !existing.cognome &&
    (!existing.nome || existing.nome === existing.nota)
  ) {
    next.nome = payload.nota;
  }

  return next;
};

const getCalendarData = ({ payload, callback, options }) =>
  sagaAssessor(
    () =>
      function* () {
        const isDemo = yield* selectIsDemoMode();
        let data = {};
        try {
          const res = yield call(() =>
            api.post('/business/prenotazioni/getCalendario', payload),
          );
          data = res?.data || {};
        } catch (err) {
          if (!isDemo) throw err;
        }
        if (isDemo) {
          const bundle = yield* getLiveDemoBundle({
            workers: data?.mypoltrone,
            services: data?.servizi,
          });
          data = {
            ...data,
            mypoltrone: data?.mypoltrone?.length ? data.mypoltrone : bundle.chairs,
            servizi: data?.servizi?.length ? data.servizi : bundle.catalog,
            prenotazioni: getDemoAppointments(bundle),
          };
        }
        yield put(schedulerActions.GET_CALENDAR_DATA.SUCCESS(data));
        callback && typeof callback === 'function' && callback();
      },
    (err) => schedulerActions.GET_CALENDAR_DATA.FAILED(err),
    options,
  );

const getCalendarQueue = ({ payload, callback, options }) =>
  sagaAssessor(
    () =>
      function* () {
        if (yield* selectIsDemoMode()) {
          const bundle = yield* getLiveDemoBundle();
          yield put(
            schedulerActions.GET_CALENDAR_QUEUE.SUCCESS(
              getDemoCalendarQueue(bundle, payload?.date),
            ),
          );
          callback && typeof callback === 'function' && callback();
          return;
        }
        const {
          data: { poltrone },
        } = yield call(() => api.post('/business/getQueueByDay', payload));
        yield put(schedulerActions.GET_CALENDAR_QUEUE.SUCCESS(poltrone));
        callback && typeof callback === 'function' && callback();
      },
    (err) => schedulerActions.GET_CALENDAR_QUEUE.FAILED(err),
    options,
  );
/**
 * Rimuove un cliente dalla coda. La riga è già sparita (reducer ottimistico su REQUEST):
 * qui si conferma, oppure si fa rollback (FAILED). In ogni caso si riallineano
 * coda calendario e coda Home dal server, senza loader.
 * callback(errorCode|null)
 */
function* removeFromQueue({ payload, callback }) {
  const { idListaAttesa, date } = payload || {};
  const silent = { skipLoader: true, silent: true };
  let errorCode = null;
  const isDemo = yield* selectIsDemoMode();
  try {
    if (isDemo) {
      markQueueRemoved(idListaAttesa);
    } else {
      yield call(() => api.post(QUEUE_REMOVE_ENDPOINT, { idListaAttesa }));
    }
    yield put(schedulerActions.REMOVE_FROM_QUEUE.SUCCESS({ idListaAttesa }));
    yield put(
      toastActions.OPEN_TOAST.SUCCESS({
        type: 'api',
        props: {
          title: t('scheduler.queue.remove.toastTitle'),
          message: t('scheduler.queue.remove.success'),
        },
      }),
    );
  } catch (error) {
    const data = error?.response?.data;
    const code =
      (data && typeof data === 'object' && (data.message || data.error || data.msg)) || null;
    if (code === QUEUE_ENTRY_NOT_FOUND) {
      // Già rimossa altrove: stato finale coerente, nessun rollback.
      yield put(schedulerActions.REMOVE_FROM_QUEUE.SUCCESS({ idListaAttesa }));
      yield put(
        toastActions.OPEN_TOAST.SUCCESS({
          type: 'api',
          props: {
            title: t('scheduler.queue.remove.toastTitle'),
            message: t('scheduler.queue.remove.errorNotFound'),
            warning: true,
          },
        }),
      );
    } else {
      errorCode = code || 'queueRemoveFailed';
      yield put(schedulerActions.REMOVE_FROM_QUEUE.FAILED(errorCode));
      yield put(setError(code === 'forbidden' ? 'notAuthorized' : 'queueRemoveFailed'));
    }
  } finally {
    const refreshDate = date || (yield select((state) => state.schedulerReducer?.queueDate));
    if (refreshDate) {
      yield put(schedulerActions.GET_CALENDAR_QUEUE.REQUEST({ date: refreshDate }, null, silent));
    }
    yield put(homeActions.GET_QUEUE.REQUEST({}, null, silent));
  }
  if (typeof callback === 'function') callback(errorCode);
}

const getCalendarSlots = ({ payload, callback, options }) =>
  sagaAssessor(
    () =>
      function* () {
        if (yield* selectIsDemoMode()) {
          const bundle = yield* getLiveDemoBundle();
          yield put(
            schedulerActions.GET_CALENDAR_SLOTS.SUCCESS(
              getDemoCalendarSlots(bundle, payload),
            ),
          );
          callback && typeof callback === 'function' && callback();
          return;
        }
        const { data } = yield call(() => api.post('/business/prenotazioni/getOrari', payload));
        yield put(schedulerActions.GET_CALENDAR_SLOTS.SUCCESS(data?.orari));
        callback && typeof callback === 'function' && callback();
      },
    (err) => schedulerActions.GET_CALENDAR_SLOTS.FAILED(err),
    options,
  );
const editCalendarTask = ({ payload, callback, options }) =>
  sagaAssessor(
    () =>
      function* () {
        const appointments = yield select(
          (state) => state.schedulerReducer?.calendar?.appointments || {},
        );
        const existing = findAppointmentById(appointments, payload.idPrenotazione);
        const snapshot = existing ? { ...existing } : null;

        // Update istantaneo della card (poi confermato/riallineato dalla response).
        if (existing) {
          yield put(
            schedulerActions.EDIT_CALENDAR_TASK.SUCCESS(
              buildOptimisticEditPayload(existing, payload),
            ),
          );
        }

        try {
          if (yield* selectIsDemoMode()) {
            const patched = buildOptimisticEditPayload(existing || {}, payload);
            patchDemoAppointment(payload.idPrenotazione, patched);
            yield put(schedulerActions.EDIT_CALENDAR_TASK.SUCCESS(patched));
            callback && typeof callback === 'function' && callback();
            return;
          }
          const { data } = yield call(() => api.post('/business/prenotazioni/updatePren', payload));
          const prenotazione = { ...(data?.prenotazione || {}) };

          if (payload.nota !== undefined) {
            prenotazione.nota = payload.nota;
            const hadNoteAsName =
              existing &&
              !existing.cognome &&
              (!existing.nome || existing.nome === existing.nota);
            if (hadNoteAsName || (!prenotazione.cognome && !prenotazione.nome)) {
              prenotazione.nome = payload.nota;
            }
          }

          yield put(
            schedulerActions.EDIT_CALENDAR_TASK.SUCCESS({
              ...existing,
              ...prenotazione,
              idprenotazione:
                prenotazione.idprenotazione ??
                existing?.idprenotazione ??
                payload.idPrenotazione,
              nota: payload.nota !== undefined ? payload.nota : prenotazione.nota,
            }),
          );
          callback && typeof callback === 'function' && callback();
        } catch (error) {
          if (snapshot) {
            yield put(schedulerActions.EDIT_CALENDAR_TASK.SUCCESS(snapshot));
          }
          options?.errCallback?.();
          throw error;
        }
      },
    (err) => schedulerActions.EDIT_CALENDAR_TASK.FAILED(err),
    options,
  );

const addCalendarTask = ({ payload, callback, options }) =>
  sagaAssessor(
    () =>
      function* () {
        try {
          if (yield* selectIsDemoMode()) {
            const bundle = yield* getLiveDemoBundle();
            const client = findDemoClient(bundle, payload.idUtente);
            const task = buildLocalAppointment(payload, client);
            yield put(schedulerActions.ADD_CALENDAR_TASK.SUCCESS([task]));
            callback && typeof callback === 'function' && callback();
            return;
          }
          const { data } = yield call(() =>
            api.post('/business/prenotazioni/setPrenotazioneSelf', payload),
          );
          const tasks = data.prenotazioni ? data.prenotazioni : [data.prenotazione];
          yield put(schedulerActions.ADD_CALENDAR_TASK.SUCCESS(tasks));
          callback && typeof callback === 'function' && callback();
        } catch (error) {
          if (error.response?.data?.stringCode === 'orarioNonDisponibile') {
            const values = {
              ...options?.values,
              saveAnyway: true,
            };
            options?.errCallback?.(values);
          } else {
            yield put(setError(error?.response?.data?.stringCode));
            // Sblocca lo Spinner del modal (es. cannotPrenInThePast)
            options?.errCallback?.();
          }
        }
      },
    // (err) => {
    //   schedulerActions.ADD_CALENDAR_TASK.FAILED(err);
    // },
  );
const addCalendarBreak = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        if (yield* selectIsDemoMode()) {
          yield put(schedulerActions.ADD_CALENDAR_BREAK.SUCCESS(payload));
          callback && typeof callback === 'function' && callback();
          return;
        }
        const { data } = yield call(() => api.post('/business/prenotazioni/takeABreak', payload));
        // const tasks = data.prenotazioni ? data.prenotazioni : [data.prenotazione]
        yield put(schedulerActions.ADD_CALENDAR_BREAK.SUCCESS(payload));
        callback && typeof callback === 'function' && callback();
      },
    (err) => schedulerActions.ADD_CALENDAR_BREAK.FAILED(err),
  );
const deleteCalendarTask = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        if (yield* selectIsDemoMode()) {
          markAppointmentDeleted(payload.idPrenotazione);
          yield put(
            schedulerActions.DELETE_CALENDAR_TASK.SUCCESS({
              id: payload.idPrenotazione,
              all: payload.deleteAllRicorrenti,
              ricorrente: payload.ricorrente,
            }),
          );
          callback && typeof callback === 'function' && callback();
          return;
        }
        yield call(() => api.post('/business/prenotazioni/disable', payload));
        yield put(
          schedulerActions.DELETE_CALENDAR_TASK.SUCCESS({
            id: payload.idPrenotazione,
            all: payload.deleteAllRicorrenti,
            ricorrente: payload.ricorrente,
          }),
        );
        callback && typeof callback === 'function' && callback();
      },
    (err) => schedulerActions.DELETE_CALENDAR_TASK.FAILED(err),
  );

const setWorkerExtraWorkingTime = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        yield call(() => api.post('/business/poltrone/addOrarioMultiply', payload));
        yield put(schedulerActions.SET_CALENDAR_WORKER_EXTRA_WORKING_TIME.SUCCESS(payload));
        callback && typeof callback === 'function' && callback();
        yield put(
          toastActions.OPEN_TOAST.SUCCESS({
            type: 'worker',
            props: { title: t('common.toastMessages.extraOpeningAdded') },
          }),
        );
      },
    (err) => schedulerActions.SET_CALENDAR_WORKER_EXTRA_WORKING_TIME.FAILED(err),
  );

const setWorkerClosures = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        yield call(() => api.post('/business/poltrone/addChiusuraMultiply', payload));
        yield put(schedulerActions.SET_CALENDAR_WORKER_CLOSURES.SUCCESS(payload));
        callback && typeof callback === 'function' && callback();
        yield put(
          toastActions.OPEN_TOAST.SUCCESS({
            type: 'worker',
            props: { title: t('common.toastMessages.extraClosureAdded') },
          }),
        );
      },
    (err) => schedulerActions.SET_CALENDAR_WORKER_CLOSURES.FAILED(err),
  );

const getDetailsPrenotazioneRicorrente = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        const responce = yield call(() =>
          api.get('/business/prenotazioni/dettaglioRicorrente/' + payload.guidRicorrenza),
        );
        yield put(schedulerActions.GET_DETAILS_PRENOTAZIONE_RICORRENTE.SUCCESS(responce?.data));
        callback && typeof callback === 'function' && callback();
      },
    (err) => schedulerActions.GET_DETAILS_PRENOTAZIONE_RICORRENTE.FAILED(err),
  );

export default function* () {
  yield takeLatest(schedulerTypes.GET_CALENDAR_DATA.REQUEST, getCalendarData);
  yield takeLatest(schedulerTypes.EDIT_CALENDAR_TASK.REQUEST, editCalendarTask);
  yield takeLatest(schedulerTypes.ADD_CALENDAR_TASK.REQUEST, addCalendarTask);
  yield takeLatest(schedulerTypes.ADD_CALENDAR_BREAK.REQUEST, addCalendarBreak);
  yield takeLatest(schedulerTypes.DELETE_CALENDAR_TASK.REQUEST, deleteCalendarTask);
  yield takeLatest(schedulerTypes.GET_CALENDAR_QUEUE.REQUEST, getCalendarQueue);
  yield takeEvery(schedulerTypes.REMOVE_FROM_QUEUE.REQUEST, removeFromQueue);
  yield takeLatest(
    schedulerTypes.SET_CALENDAR_WORKER_EXTRA_WORKING_TIME.REQUEST,
    setWorkerExtraWorkingTime,
  );
  yield takeLatest(schedulerTypes.GET_CALENDAR_SLOTS.REQUEST, getCalendarSlots);
  yield takeLatest(schedulerTypes.SET_CALENDAR_WORKER_CLOSURES.REQUEST, setWorkerClosures);
  yield takeLatest(
    schedulerTypes.GET_DETAILS_PRENOTAZIONE_RICORRENTE.REQUEST,
    getDetailsPrenotazioneRicorrente,
  );
}
